import type { ChangeEvent, ReactNode, Ref } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Table,
  useTable,
  type TableColumn,
  type TableRow,
} from '~/components/ui/table';
import { SearchInput } from '~/components/common/SearchInput';
import { sellerLabel } from '~/utils';

type OrganizationSummary = {
  id: string;
  name: string;
  sellingOrganizationCode?: string | null;
};

type Props<Organization extends OrganizationSummary> = {
  organizations: Organization[];
  tableName: string;
  isLoading: boolean;
  totalElements: number;
  sentinelRef: Ref<HTMLDivElement>;
  searchInputValue: string;
  onSearchChange: (event: ChangeEvent<HTMLInputElement>) => void;
  onOpenOrganization: (organization: Organization) => void;
  headerActions?: ReactNode;
};

export default function OrganizationsTableCard<
  Organization extends OrganizationSummary,
>({
  organizations,
  tableName,
  isLoading,
  totalElements,
  sentinelRef,
  searchInputValue,
  onSearchChange,
  onOpenOrganization,
  headerActions,
}: Props<Organization>) {
  const { t } = useTranslation('organizations');
  const tableControls = useTable({});

  const columns: TableColumn[] = [
    { key: 'name', header: t('organizations.table.name'), width: 20 },
    { key: 'view', header: '', width: 6, stickTo: 'right' },
  ];

  const rows: TableRow[] = organizations.map((organization) => ({
    id: { value: organization.id },
    name: {
      value: (
        <button
          type="button"
          className="font-medium text-neut-prim hover:underline text-left"
          onClick={() => onOpenOrganization(organization)}
        >
          {sellerLabel(organization)}
        </button>
      ),
    },
    view: {
      value: (
        <button
          type="button"
          className="text-xs font-medium text-neut-sec hover:text-neut-prim hover:underline whitespace-nowrap"
          onClick={() => onOpenOrganization(organization)}
        >
          {t('organizations.table.viewLink')}
        </button>
      ),
    },
  }));

  return (
    <div className="bg-neut-bg-alt rounded-lg border border-neut-border shadow-sm overflow-hidden">
      <div className="px-6 py-4 border-b border-neut-border flex flex-wrap items-center gap-3">
        <SearchInput
          value={searchInputValue}
          onChange={onSearchChange}
          placeholder={t('organizations.search.placeholder')}
          ariaLabel={t('organizations.search.aria_label')}
        />
        {headerActions && <div className="ml-auto">{headerActions}</div>}
      </div>

      {isLoading && organizations.length === 0 ? (
        <div className="p-8 text-center text-neut-sec text-sm">
          {t('organizations.table.loading')}
        </div>
      ) : (
        <Table
          minWidth={60}
          rows={rows}
          columns={columns}
          tableControls={tableControls}
          emptyMessage={t('organizations.table.empty')}
          totalRowsCount={totalElements}
          name={tableName}
        />
      )}
      <div ref={sentinelRef} />
    </div>
  );
}
